import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    Box,
    Heading,
    Text,
    Button,
    Input,
    FormLabel,
    Spinner,
    useToast,
    FormControl,
    Flex,
    FormErrorMessage,
    Switch,
    SimpleGrid,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Select,
    Divider,
} from "@chakra-ui/react";

const API_BASE_URL = "https://qaapirgeneng.digitalfields.co.za/ws1.cfc";

const User = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const toast = useToast();

    const HuserID = localStorage.getItem("HuserID");
    const UUID = localStorage.getItem("UUID");

    // Form state for user details
    const [userForm, setUserForm] = useState({
        fname: '',
        sname: '',
        email: '',
        cellNumber: '',
        trole: '',
        tclient: '',
        activeYN: false
    });

    // Validation state
    const [validationErrors, setValidationErrors] = useState({
        fname: '',
        sname: '',
        email: ''
    });

    // Menu options state
    const [userMenuOptions, setUserMenuOptions] = useState([]);
    const [allMenuOptions, setAllMenuOptions] = useState([]);
    const [selectedMenuID, setSelectedMenuID] = useState("");
    const [defaultPage, setDefaultPage] = useState(false);
    const [menuLoading, setMenuLoading] = useState(false);
    const [menuActionLoading, setMenuActionLoading] = useState(false);

    // Fetch user details
    const fetchUserDetails = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${API_BASE_URL}?method=getUserDets&huserID=${HuserID}&uuid=${UUID}&targetUserID=${id}`,
                {
                    method: "GET",
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            if (data && data[0]) {
                setUser(data[0]);
                setUserForm({
                    fname: data[0].fname || '',
                    sname: data[0].sname || '',
                    email: data[0].email || '',
                    cellNumber: data[0].cellNumber || '',
                    trole: data[0].trole || '',
                    tclient: data[0].tclient || '',
                    activeYN: data[0].activeYN === 1
                });
            }
        } catch (error) {
            console.error("Error fetching user details:", error);
            toast({
                title: "Error fetching user details",
                description: "There was an error fetching the user. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchUserMenuOptions = async () => {
        try {
            setMenuLoading(true);
            const response = await fetch(
                `${API_BASE_URL}?method=getUserMenuOptions&UUID=${UUID}&HuserID=${HuserID}&targetUserID=${id}`,
                { method: "POST" }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setUserMenuOptions(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error fetching user menu options:", error);
            toast({
                title: "Error fetching menu options",
                description: "Could not load this user's menu options.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setMenuLoading(false);
        }
    };

    const fetchAllMenuOptions = async () => {
        try {
            const response = await fetch(
                `${API_BASE_URL}?method=getMenuOptions&UUID=${UUID}&HuserID=${HuserID}`,
                { method: "POST" }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setAllMenuOptions(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error fetching all menu options:", error);
            toast({
                title: "Error fetching available menus",
                description: "Could not load available menu options.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    useEffect(() => {
        fetchUserDetails();
        fetchUserMenuOptions();
        fetchAllMenuOptions();
    }, [id]);

    // Restore scroll position when component mounts
    useEffect(() => {
        if (location.state?.scrollPosition && window.history.state?.scrollPosition) {
            window.scrollTo(0, window.history.state.scrollPosition);
        }
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUserForm(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear validation error for this field
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSwitchChange = (e) => {
        setUserForm(prev => ({
            ...prev,
            activeYN: e.target.checked
        }));
    };

    const validateForm = () => {
        const errors = {};
        let isValid = true;

        if (!userForm.fname.trim()) {
            errors.fname = 'First name is required';
            isValid = false;
        }

        if (!userForm.sname.trim()) {
            errors.sname = 'Last name is required';
            isValid = false;
        }

        if (!userForm.email.trim()) {
            errors.email = 'Email is required';
            isValid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userForm.email)) {
            errors.email = 'Please enter a valid email address';
            isValid = false;
        }

        setValidationErrors(errors);
        return isValid;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setSaving(true);
            const response = await fetch(
                `${API_BASE_URL}?method=updUserDets&huserID=${HuserID}&uuid=${UUID}&targetUserID=${id}`,
                {
                    method: "POST",
                    body: JSON.stringify({
                        fname: userForm.fname,
                        sname: userForm.sname,
                        email: userForm.email,
                        cellNumber: userForm.cellNumber,
                        trole: userForm.trole,
                        tclient: userForm.tclient,
                        activeYN: userForm.activeYN ? 1 : 0
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            toast({
                title: "User updated successfully",
                description: "The user details have been saved.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });

            // Refresh user data
            fetchUserDetails();
        } catch (error) {
            console.error("Error saving user:", error);
            toast({
                title: "Error saving user",
                description: error.message || "There was an error saving the user. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        // Reset form to original values
        if (user) {
            setUserForm({
                fname: user.fname || '',
                sname: user.sname || '',
                email: user.email || '',
                cellNumber: user.cellNumber || '',
                trole: user.trole || '',
                tclient: user.tclient || '',
                activeYN: user.activeYN === 1
            });
        }
        setValidationErrors({
            fname: '',
            sname: '',
            email: ''
        });
    };

    const handleBack = () => {
        navigate('/users', {
            state: { scrollPosition: location.state?.scrollPosition }
        });
    };

    const availableMenuOptions = allMenuOptions.filter(
        (option) => !userMenuOptions.some((assigned) => assigned.HmenuID === option.HmenuID)
    );

    const handleAddMenuOption = async () => {
        if (!selectedMenuID) {
            toast({
                title: "Select a menu option",
                description: "Please choose a menu option to add.",
                status: "warning",
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        try {
            setMenuActionLoading(true);
            const response = await fetch(
                `${API_BASE_URL}?method=addUserMenuOption&UUID=${UUID}&HuserID=${HuserID}&targetUserID=${id}&HmenuID=${selectedMenuID}&defaultpage=${defaultPage ? 1 : 0}`,
                { method: "POST" }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            toast({
                title: "Menu option added",
                description: "The menu option has been assigned to this user.",
                status: "success",
                duration: 4000,
                isClosable: true,
            });

            setSelectedMenuID("");
            setDefaultPage(false);
            await fetchUserMenuOptions();
        } catch (error) {
            console.error("Error adding menu option:", error);
            toast({
                title: "Error adding menu option",
                description: error.message || "Could not add the menu option. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setMenuActionLoading(false);
        }
    };

    const handleRemoveMenuOption = async (HmenuID) => {
        try {
            setMenuActionLoading(true);
            const response = await fetch(
                `${API_BASE_URL}?method=removeUserMenuOption&UUID=${UUID}&HuserID=${HuserID}&targetUserID=${id}&HmenuID=${HmenuID}`,
                { method: "POST" }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            toast({
                title: "Menu option removed",
                description: "The menu option has been removed from this user.",
                status: "success",
                duration: 4000,
                isClosable: true,
            });

            await fetchUserMenuOptions();
        } catch (error) {
            console.error("Error removing menu option:", error);
            toast({
                title: "Error removing menu option",
                description: error.message || "Could not remove the menu option. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setMenuActionLoading(false);
        }
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
            >
                <Spinner size="xl" />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box p={5}>
                <Heading mb={5}>User Not Found</Heading>
                <Button onClick={handleBack}>Back to Users</Button>
            </Box>
        );
    }

    return (
        <Box p={5}>
            <Flex align="center" mb={5}>
                <Button onClick={handleBack} mr={4}>
                    ← Back to Users
                </Button>
                <Heading>User Details</Heading>
            </Flex>

            <Box
                as="form"
                maxW="1200px"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSave();
                }}
            >
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {/* First Name */}
                    <FormControl isInvalid={!!validationErrors.fname}>
                        <FormLabel>First Name</FormLabel>
                        <Input
                            name="fname"
                            value={userForm.fname}
                            onChange={handleInputChange}
                            placeholder="First Name"
                        />
                        <FormErrorMessage>{validationErrors.fname}</FormErrorMessage>
                    </FormControl>

                    {/* Last Name */}
                    <FormControl isInvalid={!!validationErrors.sname}>
                        <FormLabel>Last Name</FormLabel>
                        <Input
                            name="sname"
                            value={userForm.sname}
                            onChange={handleInputChange}
                            placeholder="Last Name"
                        />
                        <FormErrorMessage>{validationErrors.sname}</FormErrorMessage>
                    </FormControl>

                    {/* Email */}
                    <FormControl isInvalid={!!validationErrors.email}>
                        <FormLabel>Email</FormLabel>
                        <Input
                            name="email"
                            type="email"
                            value={userForm.email}
                            onChange={handleInputChange}
                            placeholder="Email"
                        />
                        <FormErrorMessage>{validationErrors.email}</FormErrorMessage>
                    </FormControl>

                    {/* Cell Number */}
                    <FormControl>
                        <FormLabel>Cell Number</FormLabel>
                        <Input
                            name="cellNumber"
                            type="tel"
                            value={userForm.cellNumber}
                            onChange={handleInputChange}
                            placeholder="Cell Number"
                        />
                    </FormControl>

                    {/* Role (Read-only) */}
                    <FormControl>
                        <FormLabel>Role</FormLabel>
                        <Text>{userForm.trole || "-"}</Text>
                    </FormControl>

                    {/* Client (Read-only) */}
                    <FormControl>
                        <FormLabel>Client</FormLabel>
                        <Text>{userForm.tclient || "-"}</Text>
                    </FormControl>

                    {/* User ID (Read-only) */}
                    <FormControl>
                        <FormLabel>User ID</FormLabel>
                        <Text>{user.HuserID}</Text>
                    </FormControl>

                    {/* Active Status */}
                    <FormControl display="flex" alignItems="center">
                        <FormLabel htmlFor="activeYN" mb="0">
                            Active Status
                        </FormLabel>
                        <Switch
                            id="activeYN"
                            isChecked={userForm.activeYN}
                            onChange={handleSwitchChange}
                            colorScheme="green"
                        />
                    </FormControl>
                </SimpleGrid>

                {/* Action Buttons */}
                <Flex gap={2} mt={6}>
                    <Button
                        type="submit"
                        variant="outline-rounded"
                        isLoading={saving}
                        loadingText="Saving..."
                    >
                        Save
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                </Flex>
            </Box>

            <Divider my={8} />

            {/* Menu Options */}
            <Box maxW="1200px">
                <Heading size="md" mb={4}>
                    Menu Options
                </Heading>
                <Text mb={4} color="gray.600">
                    Manage which menu items this user can access.
                </Text>

                {menuLoading ? (
                    <Flex justify="center" py={6}>
                        <Spinner />
                    </Flex>
                ) : (
                    <Table variant="simple" mb={6} size="sm">
                        <Thead>
                            <Tr>
                                <Th>Display Name</Th>
                                <Th>Code</Th>
                                <Th>Route</Th>
                                <Th>Order</Th>
                                <Th>Default</Th>
                                <Th>Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {userMenuOptions.length === 0 ? (
                                <Tr>
                                    <Td colSpan={6}>
                                        <Text color="gray.500">No menu options assigned.</Text>
                                    </Td>
                                </Tr>
                            ) : (
                                userMenuOptions
                                    .slice()
                                    .sort((a, b) => (a.displayorder || 0) - (b.displayorder || 0))
                                    .map((option) => (
                                        <Tr key={option.HmenuID}>
                                            <Td>{option.displayname}</Td>
                                            <Td>{option.menuCode}</Td>
                                            <Td>{option.route}</Td>
                                            <Td>{option.displayorder}</Td>
                                            <Td>{option.defaultpage === 1 ? "Yes" : "No"}</Td>
                                            <Td>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    colorScheme="red"
                                                    isDisabled={menuActionLoading}
                                                    onClick={() => handleRemoveMenuOption(option.HmenuID)}
                                                >
                                                    Remove
                                                </Button>
                                            </Td>
                                        </Tr>
                                    ))
                            )}
                        </Tbody>
                    </Table>
                )}

                <Flex
                    gap={4}
                    align={{ base: "stretch", md: "flex-end" }}
                    direction={{ base: "column", md: "row" }}
                    maxW="700px"
                >
                    <FormControl>
                        <FormLabel>Add Menu Option</FormLabel>
                        <Select
                            placeholder="Select a menu option"
                            value={selectedMenuID}
                            onChange={(e) => setSelectedMenuID(e.target.value)}
                        >
                            {availableMenuOptions.map((option) => (
                                <option key={option.HmenuID} value={option.HmenuID}>
                                    {option.displayname} ({option.menuCode})
                                </option>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl display="flex" alignItems="center" w="auto" minW="140px" mb={{ base: 0, md: 2 }}>
                        <FormLabel htmlFor="defaultpage" mb="0" whiteSpace="nowrap">
                            Default page
                        </FormLabel>
                        <Switch
                            id="defaultpage"
                            isChecked={defaultPage}
                            onChange={(e) => setDefaultPage(e.target.checked)}
                            colorScheme="green"
                        />
                    </FormControl>

                    <Button
                        variant="outline-rounded"
                        onClick={handleAddMenuOption}
                        isLoading={menuActionLoading}
                        isDisabled={!selectedMenuID || availableMenuOptions.length === 0}
                        flexShrink={0}
                    >
                        Add
                    </Button>
                </Flex>

                {availableMenuOptions.length === 0 && !menuLoading && (
                    <Text mt={2} fontSize="sm" color="gray.500">
                        All available menu options are already assigned to this user.
                    </Text>
                )}
            </Box>
        </Box>
    );
};

export default User;
